import React, { useEffect, useState, useMemo, useCallback } from "react";
import { FIELD_LIMITS, getFormatRules, validateBlocks } from "../validators";
import type { FormatRules } from "../validators";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import "../studio-dialog.css";

interface ManualEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (blocks: any[]) => void;
  blocks: any[];
  saving?: boolean;
  saveError?: string | null;
  versionNumber?: number;
  contentFormatSlug?: string;
}

interface EditableBlock {
  id: string;
  block_type: string;
  title: string;
  narration: string;
  caption: string;
  estimated_duration: number;
  original: any;
  _isNew?: boolean;
}

const BLOCK_FIELDS: { key: "title" | "narration" | "caption"; label: string }[] = [
  { key: "title", label: "Title" },
  { key: "narration", label: "Narration" },
  { key: "caption", label: "Caption" },
];

let _newBlockCounter = 0;
function nextNewId(blockType: string): string {
  _newBlockCounter += 1;
  return `new_${blockType}_${_newBlockCounter}_${Date.now()}`;
}

const ManualEditDialog: React.FC<ManualEditDialogProps> = ({
  open,
  onClose,
  onSave,
  blocks,
  saving,
  saveError,
  versionNumber,
  contentFormatSlug,
}) => {
  const [editedBlocks, setEditedBlocks] = useState<EditableBlock[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const formatRules = useMemo(() => getFormatRules(contentFormatSlug), [contentFormatSlug]);

  useEffect(() => {
    if (open) {
      _newBlockCounter = 0;
      setEditedBlocks(
        blocks.map((b: any, i: number) => ({
          id: b.id || `block_${i}`,
          block_type: b.block_type,
          title: b.title || "",
          narration: b.narration || "",
          caption: b.caption || "",
          estimated_duration: Math.round(parseFloat(b.estimated_duration) || 0),
          original: { ...b },
        }))
      );
      setCollapsed(new Set());
      setRemovedIds(new Set());
    }
  }, [open, blocks]);

  useEffect(() => {
    if (open) {
      const bodyOverflow = document.body.style.overflow;
      const htmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = bodyOverflow;
        document.documentElement.style.overflow = htmlOverflow;
      };
    }
  }, [open]);

  const handleFieldChange = useCallback(
    (blockId: string, field: "title" | "narration" | "caption", value: string) => {
      setEditedBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== blockId) return b;
          const updated = { ...b, [field]: value };
          if (field === "narration") {
            updated.estimated_duration = Math.max(1, Math.round(value.length / 15));
          }
          return updated;
        })
      );
    },
    []
  );

  const handleRestoreField = useCallback((blockId: string, field: "title" | "narration" | "caption") => {
    setEditedBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const narration = field === "narration" ? (b.original.narration || "") : b.narration;
        return {
          ...b,
          [field]: b.original[field] || "",
          estimated_duration: Math.max(1, Math.round(narration.length / 15)),
        };
      })
    );
  }, []);

  const handleRemoveBlock = useCallback((blockId: string) => {
    setEditedBlocks((prev) => prev.filter((b) => b.id !== blockId));
    setRemovedIds((prev) => new Set(prev).add(blockId));
  }, []);

  const handleAddBlock = useCallback((blockType: string) => {
    const newBlock: EditableBlock = {
      id: nextNewId(blockType),
      block_type: blockType,
      title: "",
      narration: "",
      caption: "",
      estimated_duration: 0,
      original: {},
      _isNew: true,
    };

    setEditedBlocks((prev) => {
      const idx = prev.findIndex((b) => b.block_type === "conclusion" || b.block_type === "cta");
      if (idx >= 0) {
        const copy = [...prev];
        copy.splice(idx, 0, newBlock);
        return copy;
      }
      return [...prev, newBlock];
    });
  }, []);

  const isModified = useMemo(() => {
    if (removedIds.size > 0) return true;
    return editedBlocks.some(
      (b) =>
        b._isNew ||
        b.title !== (b.original.title || "") ||
        b.narration !== (b.original.narration || "") ||
        b.caption !== (b.original.caption || "")
    );
  }, [editedBlocks, removedIds]);

  const validationErrors = useMemo(() => validateBlocks(editedBlocks, formatRules), [editedBlocks, formatRules]);

  const canSave = isModified && !saving && validationErrors.length === 0;

  const toggleCollapse = useCallback((blockId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  }, []);

  const isBlockRemovable = useCallback(
    (blockType: string) => {
      if (!formatRules) return true;
      const rule = formatRules.blockTypes.find((r) => r.type === blockType);
      return rule ? rule.removable : true;
    },
    [formatRules]
  );

  const canAddBlock = useCallback(
    (blockType: string) => {
      if (!formatRules) return false;
      const rule = formatRules.blockTypes.find((r) => r.type === blockType);
      if (!rule || !rule.addable) return false;
      const count = editedBlocks.filter((b) => b.block_type === blockType).length;
      if (rule.maxCount !== undefined && count >= rule.maxCount) return false;
      return true;
    },
    [formatRules, editedBlocks]
  );

  const handleSave = () => {
    const updatedBlocks = editedBlocks.map((b) => ({
      ...(b._isNew ? {} : b.original),
      id: b.id,
      block_type: b.block_type,
      title: b.title,
      narration: b.narration,
      caption: b.caption,
      estimated_duration: b.estimated_duration,
      ...(b._isNew ? { _isNew: true } : {}),
    }));
    onSave(updatedBlocks);
  };

  return (
    <Dialog
      className="ce-studio-dialog"
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: { className: "ce-studio-dialog-backdrop" },
        paper: { className: "ce-studio-dialog-paper" },
      }}
      disableEscapeKeyDown={saving}
    >
      <div className="ce-dialog-header">
        <div className="ce-dialog-header-inner">
          <div className="ce-dialog-header-left">
            <div className="ce-dialog-icon-wrap">
              <EditOutlinedIcon sx={{ fontSize: 24 }} />
            </div>
            <div>
              <h2 className="ce-dialog-title">Manual Edit</h2>
              <span className="ce-dialog-subtitle">
                {versionNumber ? `Editing Version ${versionNumber} — ` : ""}Your changes will be saved as a new version
              </span>
            </div>
          </div>
          <IconButton
            className="ce-dialog-close"
            onClick={onClose}
            size="small"
            disabled={saving}
            aria-label="Close manual edit"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      <DialogContent className="ce-dialog-body" sx={{ overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {saveError && (
            <div className="ce-dialog-alert" style={{ marginBottom: 16 }}>
              <p className="ce-dialog-alert-title">Save failed</p>
              <p className="ce-dialog-alert-text">{saveError}</p>
            </div>
          )}
          {validationErrors.length > 0 && (
            <div className="ce-dialog-alert" style={{ marginBottom: 16 }}>
              <p className="ce-dialog-alert-title">Validation errors</p>
              {validationErrors.map((err, i) => (
                <p key={i} className="ce-dialog-alert-text">• {err}</p>
              ))}
            </div>
          )}
          {editedBlocks.map((block, bi) => {
            const fieldModified =
              block.title !== (block.original.title || "") ||
              block.narration !== (block.original.narration || "") ||
              block.caption !== (block.original.caption || "");

            return (
              <div key={block.id} className="ce-dialog-block-card">
                <div
                  className={`ce-dialog-block-header${collapsed.has(block.id) ? " collapsed" : ""}`}
                  onClick={() => toggleCollapse(block.id)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {collapsed.has(block.id) ? (
                      <ChevronRight sx={{ fontSize: 18, color: "rgba(35, 100, 232, 0.5)" }} />
                    ) : (
                      <ExpandMore sx={{ fontSize: 18, color: "rgba(35, 100, 232, 0.5)" }} />
                    )}
                    <span className="ce-dialog-block-label">
                      {formatRules?.blockTypes?.find((r) => r.type === block.block_type)?.label || block.block_type.split("_")[0]}{block.original.payload?.number ? ` #${block.original.payload.number}` : ""}
                    </span>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {block.estimated_duration > 0 && (
                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>
                        ~{block.estimated_duration}s
                      </Typography>
                    )}
                    {isBlockRemovable(block.block_type) && (
                      <DeleteOutlineIcon
                        onClick={(e) => { e.stopPropagation(); handleRemoveBlock(block.id); }}
                        sx={{
                          fontSize: 16,
                          color: "rgba(239, 68, 68, 0.4)",
                          cursor: "pointer",
                          "&:hover": { color: "#ef4444" },
                        }}
                      />
                    )}
                  </Box>
                </div>
                {!collapsed.has(block.id) && (
                <div className="ce-dialog-block-body">
                  {(["title", "narration", "caption"] as const).map((f) => {
                    const isFieldModified = block[f] !== (block.original[f] || "");
                    const maxLen = FIELD_LIMITS[f].max;
                    const remaining = maxLen - block[f].length;
                    return (
                    <div key={f}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <span className={`ce-dialog-field-label${isFieldModified ? " modified" : ""}`}>
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </span>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {isFieldModified && (
                            <RestoreOutlinedIcon
                              onClick={() => handleRestoreField(block.id, f)}
                              sx={{
                                fontSize: 12,
                                color: "rgba(239, 68, 68, 0.5)",
                                cursor: "pointer",
                                "&:hover": { color: "#ef4444" },
                              }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: remaining < 0 ? "#ef4444" : "#94a3b8",
                            }}
                          >
                            {block[f].length}/{maxLen}
                          </Typography>
                        </Box>
                      </Box>
                      {f === "narration" ? (
                        <textarea
                          className={`ce-dialog-field-textarea${isFieldModified ? " modified" : ""}`}
                          value={block[f]}
                          onChange={(e) => {
                            if (e.target.value.length <= maxLen) handleFieldChange(block.id, f, e.target.value);
                          }}
                          placeholder="Narration"
                          rows={2}
                        />
                      ) : (
                        <input
                          className={`ce-dialog-field-input${isFieldModified ? " modified" : ""}`}
                          value={block[f]}
                          onChange={(e) => {
                            if (e.target.value.length <= maxLen) handleFieldChange(block.id, f, e.target.value);
                          }}
                          placeholder={f.charAt(0).toUpperCase() + f.slice(1)}
                        />
                      )}
                    </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
          {formatRules?.blockTypes?.filter((rule) => rule.addable && canAddBlock(rule.type))?.map((rule) => (
              <Button
                key={rule.type}
                className="ce-dialog-add-btn"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleAddBlock(rule.type)}
              >
                Add {rule.label}
              </Button>
            ))}
        </Box>
      </DialogContent>

      <div className="ce-dialog-footer">
        <div className="ce-dialog-footer-grid">
          <Button
            className="ce-dialog-btn-cancel"
            variant="outlined"
            onClick={onClose}
            fullWidth
            disabled={saving}
          >
            Cancel
          </Button>
          <div>
            <Button
              className="ce-dialog-btn-primary"
              variant="contained"
              onClick={handleSave}
              fullWidth
              disabled={!canSave}
              startIcon={saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SaveOutlinedIcon sx={{ fontSize: 17 }} />}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <p className={`ce-dialog-footer-note${validationErrors.length > 0 ? " error" : ""}`}>
              {validationErrors.length > 0
                ? `Fix ${validationErrors.length} issue${validationErrors.length > 1 ? "s" : ""} to save`
                : "Previous versions are preserved"}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ManualEditDialog;
